import { GRAPH_INTERVAL } from "@const/pageResponse.constants";
import { FindDashboardItemDTO, FindItem, FindItemsDTO } from "@dto/dashboard.dto";
import { Box, GraphInterval, Metric } from "@dto/pageResponse.dto";
import { DateUtil } from "@dto/util/DateUtil";
import { AxiosError } from "axios";
import { isEqual } from "lodash-es";
import { Component } from "react";
import { CommonGraphProps, KTM, SharedYRangeProps, YExtremes } from "src/component/graph/GraphTypes";
import { API_URL } from "src/data/Api";
import { Const } from "src/data/Const";
import { postApi } from "src/util/apiCalls";
import { ColorUtil } from "src/util/ColorUtil";
import { GraphPlotUtil, GraphRangeState } from "../../component/graph/GraphPlotUtil";
import { Log } from "../../service/log.service";
import { BoxPart } from "../BoxPart";

export class GraphPartProps<T extends Box> extends BoxPart<T> {

}

export interface AbstractGraphPartState {
  dashboardGraphs: FindItem[];
  /**
   * Value for {@link SharedYRangeContext}, lets graphs with the same unit share a Y range
   */
  sharedY: SharedYRangeProps;
}

export class ColorMap {
  colorPalette: Record<string, string> = {};
  colorIndex = 0;
}

export abstract class AbstractGraphPart<P extends Box, S extends AbstractGraphPartState = AbstractGraphPartState>
  extends Component<GraphPartProps<P>, S & GraphRangeState> {

  //static readonly OTHERS_UID = 'others';
  //static readonly DELETED_UID = 'deleted';

  protected common: CommonGraphProps;
  protected interval = true;

  private timeoutShort = 0;
  private timeoutLong = 0;
  /**
   * Y extremes reported by mounted graphs, keyed by graph id
   */
  private readonly yReports = new Map<string, { unitKey: string; extremes: YExtremes; }>();
  private readonly initialSharedY: SharedYRangeProps = { extremes: {}, report: this.reportYExtremes.bind(this) };

  constructor(props: GraphPartProps<P>) {
    super(props);
    this.state = this.initialState() as Readonly<S & GraphRangeState>;
  }

  protected initialState(): Partial<S & GraphRangeState> {
    // state resets keep the shared Y range, graphs re-report it after reloading
    return { sharedY: this.initialSharedY, ...this.state, ...DateUtil.initShort(), ...DateUtil.initLong() };
  }

  private reportYExtremes(graphId: string, unitKey: string, extremes: YExtremes | null) {
    if (extremes) this.yReports.set(graphId, { unitKey, extremes });
    else this.yReports.delete(graphId);
    const combined: Record<string, YExtremes> = {};
    for (const report of this.yReports.values()) {
      const cur = combined[report.unitKey];
      combined[report.unitKey] = cur
        ? { min: Math.min(cur.min, report.extremes.min), max: Math.max(cur.max, report.extremes.max) }
        : report.extremes;
    }
    this.setState(prev => isEqual(prev.sharedY.extremes, combined) ? null : { sharedY: { ...prev.sharedY, extremes: combined } });
  }

  componentDidMount(): void {
    if (this.interval) {
      this.updateShort();
      this.updateLong();
      if (this.props.url) {
        const searchBody: FindDashboardItemDTO = { tab: this.props.tabName, subtab: this.props.subTabName, tabId: this.props.tabId, url: this.props.url, type: 'graph' };
        postApi<FindItemsDTO, FindDashboardItemDTO>(API_URL.DASHBOARD_FIND_ITEM, searchBody).then(response => {
          const found = response.data.data;
          this.common.dashboardItems = found;
          this.setState({ dashboardGraphs: found });
        }, (reason: AxiosError) => Log.error('Failed to get graph dashboards!', reason));
      }
    }
  }

  componentWillUnmount(): void {
    window.clearTimeout(this.timeoutShort);
    window.clearTimeout(this.timeoutLong);
  }

  protected getStart(interval: GraphInterval) {
    switch (interval) {
      case GRAPH_INTERVAL.day:
        return this.state.startDay;
      case GRAPH_INTERVAL.week:
        return this.state.startWeek;
      case GRAPH_INTERVAL.month:
        return this.state.startMonth;
      case GRAPH_INTERVAL.year:
        return this.state.startYear;
      default:
        Log.warn('Unknown graph interval: ' + interval);
    }
  }

  protected getEnd(interval: GraphInterval) {
    if (interval === GRAPH_INTERVAL.day)
      return this.state.endShort;
    else
      return this.state.endLong;
  }

  protected updateShort() {
    window.clearTimeout(this.timeoutShort);
    this.timeoutShort = window.setTimeout(() => {
      this.setState(prev => ({ ...prev, ...DateUtil.initShort() }));
      this.updateShort();
    }, Const.TIMEOUT_SHORT);
  }

  protected updateLong() {
    window.clearTimeout(this.timeoutLong);
    this.timeoutLong = window.setTimeout(() => {
      this.setState(prev => ({ ...prev, ...DateUtil.initLong() }));
      this.updateLong();
    }, Const.TIMEOUT_LONG);
  }

  static mapColors(uuids: string[], metrics: Metric[], aggregated: boolean, colorMap: ColorMap = new ColorMap()) {
    if (!uuids || !metrics) return colorMap;
    if (GraphPlotUtil.isMetricItems(metrics, aggregated)) {
      for (const metric of metrics) {
        colorMap.colorPalette[metric.metric] = ColorUtil.colorPalette[colorMap.colorIndex];
        colorMap.colorIndex = (colorMap.colorIndex + 1) % ColorUtil.colorPalette.length;
      }
    }
    else {
      const otherUuids = [...uuids, KTM.ITEM_OTHERS];
      for (const uuid of otherUuids) {
        for (const metric of metrics) {
          colorMap.colorPalette[uuid + metric.metric] = ColorUtil.colorPalette[colorMap.colorIndex];
          colorMap.colorIndex = (colorMap.colorIndex + 1) % ColorUtil.colorPalette.length;
        }
      }
      for (const metric of metrics) {
        colorMap.colorPalette[KTM.ITEM_DELETED + metric.metric] = ColorUtil.NO_DATA_COLOR;
      }
    }
    return colorMap;
  }
}